import { useListPartnerships } from '@/shared/hooks/api/partnerships'
import useI18n from '@/shared/hooks/useI18n'
import { useSession } from '@/shared/hooks/useSession'
import {
  BreadcrumbsBar,
  Button,
  H2,
  Partnership,
  PartnershipInviterOrganizationType,
  PartnershipStatus,
  Tab
} from '@/common'
import { PlusIcon } from '@/common/components/icons'
import useDialog from '@/common/hooks/useDialog'
import { useEffect, useState } from 'react'
import { Navigate, NavLink, Outlet, Route, useLocation } from 'react-router-dom'
import { Routes } from 'react-router-dom'
import CreateImporterDialog from './dialogs/CreateImporterDialog'
import RejectedImportersPage from './pages/rejected-importers'
import IncomingImportersPage from './pages/incoming-importers'
import ImportersListPage from './pages/importers-list'
import { Container } from '@/shared/components'

export default function ImportersPage() {
  const { t } = useI18n()
  const location = useLocation()
  const { open, closeDialog, openDialog } = useDialog()
  const defaultBreadcrumbs = [{ label: t('importers:breadcrumbs.importers'), to: '/importers' }]
  const [breadcrumbs, setBreadcrumbs] =
    useState<{ label: string; to: string; isActive?: boolean }[]>(defaultBreadcrumbs)
  const organizationId = useSession().session.organization.id

  const { data, isLoading } = useListPartnerships(organizationId)
  const partnerships = data ?? []

  useEffect(() => {
    if (location.pathname.endsWith('/connected')) {
      setBreadcrumbs([
        ...defaultBreadcrumbs,
        {
          label: t('importers:breadcrumbs.connectedImporters'),
          to: ''
        }
      ])
    } else if (location.pathname.endsWith('/pending')) {
      setBreadcrumbs([
        ...defaultBreadcrumbs,
        {
          label: t('importers:breadcrumbs.pendingInvitations'),
          to: ''
        }
      ])
    } else if (location.pathname.endsWith('/rejected')) {
      setBreadcrumbs([
        ...defaultBreadcrumbs,
        {
          label: t('importers:breadcrumbs.rejectedInvitations'),
          to: ''
        }
      ])
    }
  }, [location.pathname])

  const pendingIncomingPartnerships = partnerships.reduce<Partnership[]>((acc, partnership) => {
    if (
      partnership.invitationOrganizationType === PartnershipInviterOrganizationType.DECLARANT &&
      partnership.status === PartnershipStatus.INVITATION_SENT
    ) {
      acc.push(partnership)
    }
    return acc
  }, [])

  const rejectedIncomingPartnerships = partnerships.filter(
    partnership => partnership.status === PartnershipStatus.INVITATION_REJECTED
  )

  const hasPendingPartnerships = pendingIncomingPartnerships.length > 0

  const createdOrPendingOutgoingInvitations = partnerships.filter(partnership => {
    const { invitationOrganizationType, status } = partnership
    const isInitiatedByOperator =
      invitationOrganizationType === PartnershipInviterOrganizationType.OPERATOR
    const isCreated = status === PartnershipStatus.CREATED
    const isInvitationSent = status === PartnershipStatus.INVITATION_SENT

    if (isInitiatedByOperator && (isCreated || isInvitationSent)) {
      return true
    }
    return false
  })

  const acceptedPartnerships = partnerships.filter(partnership => {
    const { status } = partnership
    const isAccepted = status === PartnershipStatus.INVITATION_ACCEPTED

    if (isAccepted) {
      return true
    }
    return false
  })

  const tabs = (
    <div className="flex gap-4 border-b">
      <NavLink to="connected" relative="path">
        {({ isActive }) => <Tab isActive={isActive}>{t('importers:tabs.connectedImporters')}</Tab>}
      </NavLink>
      <NavLink to="pending" relative="path">
        {({ isActive }) => (
          <Tab isActive={isActive}>
            <span className="relative">
              {t('importers:tabs.incomingInvitations')}
              {hasPendingPartnerships && (
                <span className="absolute -top-1 -right-3 w-2 h-2 bg-green-600 rounded-full" />
              )}
            </span>
          </Tab>
        )}
      </NavLink>
      <NavLink to="rejected" relative="path">
        {({ isActive }) => <Tab isActive={isActive}>{t('importers:tabs.rejectedInvitations')}</Tab>}
      </NavLink>
    </div>
  )

  const layout = (
    <>
      <BreadcrumbsBar links={breadcrumbs} />
      <Container>
        <header className="flex justify-between items-start">
          <H2>{t('importers:title')}</H2>
          <Button onClick={openDialog} icon={<PlusIcon />}>
            {t('importers:actions.addImporter')}
          </Button>
        </header>
        {tabs}
        <Outlet />
      </Container>
      <CreateImporterDialog isOpen={open} close={closeDialog} />
    </>
  )

  return (
    <Routes>
      <Route path="/" element={<Navigate to="connected" />} />
      <Route path="/" element={layout}>
        <Route
          path="/connected"
          element={
            <ImportersListPage
              createdOrPendingOutgoingInvitations={createdOrPendingOutgoingInvitations}
              acceptedPartnerships={acceptedPartnerships}
              isLoading={isLoading}
            />
          }
        />
        <Route
          path="/pending"
          element={
            <IncomingImportersPage
              pendingPartnerships={pendingIncomingPartnerships}
              isLoading={isLoading}
            />
          }
        />
        <Route
          path="/rejected"
          element={
            <RejectedImportersPage
              rejectedPartnerships={rejectedIncomingPartnerships}
              isLoading={isLoading}
            />
          }
        />
      </Route>
      {/* <Route path="/:id" element={<ImporterDetailsPage />} /> */}
    </Routes>
  )
}
